/* tslint:disable */
/* eslint-disable */
/**
 * Scrivio Audit API
 * API for querying the unified audit log within an organization. The audit log records both mutation events (create, update, delete) and access events (view, download) for compliance and accountability purposes. All operations are scoped to the authenticated user\'s organization. 
 *
 * The version of the OpenAPI document: 1.0.0
 * 
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import type { Configuration } from './configuration';
import type { AxiosPromise, AxiosInstance, RawAxiosRequestConfig } from 'axios';
import globalAxios from 'axios';
// Some imports not used depending on template conditions
// @ts-ignore
import { DUMMY_BASE_URL, assertParamExists, setApiKeyToObject, setBasicAuthToObject, setBearerAuthToObject, setOAuthToObject, setSearchParams, serializeDataIfNeeded, toPathString, createRequestFunction } from './common';
import type { RequestArgs } from './base';
// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS, BaseAPI, RequiredError, operationServerMap } from './base';

/**
 * Type of actor who performed the action: - USER: A behandelaar or organization user - CLIENT: A client accessing via the client portal - ADMIN: An admin-portal actor. Admin users live in `admin_user`, not   `users`, so such an entry carries `adminUserId` and no `userId`. An   admin who reached the data by impersonating a user is a USER entry   with `impersonatedByUserId` set, not this. 
 */

export const AccessorType = {
    User: 'USER',
    Client: 'CLIENT',
    Admin: 'ADMIN'
} as const;

export type AccessorType = typeof AccessorType[keyof typeof AccessorType];


/**
 * Actions that can be logged in the audit log:  **Mutation actions:** - CREATE: Resource was created - UPDATE: Resource was modified - DELETE: Resource was permanently deleted - ARCHIVE: Resource was archived (soft delete) - RESTORE: Resource was restored from archive - STATUS_CHANGE: Resource status was changed - FINALIZE: Resource was finalized (e.g., report locked) - EXPOSE: Resource was made visible to client portal - UNEXPOSE: Resource was hidden from client portal - ACTIVATE: Resource was activated - DEPRECATE: Resource was deprecated - SUBMIT: Resource was submitted (e.g., form submission) - ACKNOWLEDGE: Client explicitly acknowledged a document via the portal - SEND: Resource was disclosed to an external party (e.g., letter sent via Zivver) - PURGE: Bulk erasure of operational dossier data via the dossier-purge engine  **Access actions:** - VIEW: Resource was viewed - LIST: Resource list was accessed - DOWNLOAD: Resource was downloaded - EXPORT: Resource was exported 
 */

export const AuditAction = {
    Create: 'CREATE',
    Update: 'UPDATE',
    Delete: 'DELETE',
    Archive: 'ARCHIVE',
    Restore: 'RESTORE',
    StatusChange: 'STATUS_CHANGE',
    Finalize: 'FINALIZE',
    Expose: 'EXPOSE',
    Unexpose: 'UNEXPOSE',
    Activate: 'ACTIVATE',
    Deprecate: 'DEPRECATE',
    Submit: 'SUBMIT',
    Acknowledge: 'ACKNOWLEDGE',
    Send: 'SEND',
    Purge: 'PURGE',
    View: 'VIEW',
    List: 'LIST',
    Download: 'DOWNLOAD',
    Export: 'EXPORT'
} as const;

export type AuditAction = typeof AuditAction[keyof typeof AuditAction];


/**
 * What kind of actor performed the action, beside `accessorType`: - PERSON: a person — a user, a cliënt or an admin - API_KOPPELING: an API-koppeling acting as its service user; `apiKoppeling` names it  A person acting through a koppeling on their own behalf is a PERSON with `apiKoppeling` set alongside, naming the koppeling the access came through. 
 */

export const AuditActorKind = {
    Person: 'PERSON',
    ApiKoppeling: 'API_KOPPELING'
} as const;

export type AuditActorKind = typeof AuditActorKind[keyof typeof AuditActorKind];


/**
 * The API-koppeling an access came through
 */
export interface AuditApiKoppeling {
    /**
     * API-koppeling identifier
     */
    'id': string;
    /**
     * The name the organisation gave the koppeling
     */
    'name': string;
}
/**
 * A single audit log entry
 */
export interface AuditLogEntry {
    /**
     * Unique audit log entry identifier
     */
    'id': string;
    /**
     * When the action occurred (ISO 8601)
     */
    'occurredAt': string;
    'accessorType': AccessorType;
    'actorKind': AuditActorKind;
    'apiKoppeling'?: AuditApiKoppeling;
    /**
     * ID of the user who performed the action (when accessorType is USER)
     */
    'userId'?: string | null;
    /**
     * Display name of the user who performed the action
     */
    'userName'?: string | null;
    /**
     * ID of the client who performed the action (when accessorType is CLIENT)
     */
    'clientAccessorId'?: string | null;
    /**
     * ID of the admin-portal user who performed the action (when accessorType is ADMIN). Resolves against the `admin_user` table. Null when the admin-portal actor could not be named — an unknown actor is recorded as unknown rather than attributed to somebody. 
     */
    'adminUserId'?: string | null;
    'action': AuditAction;
    /**
     * Type of resource that was acted upon
     */
    'resourceType': string;
    /**
     * ID of the resource that was acted upon
     */
    'resourceId': string;
    /**
     * Human-readable label for the resource (e.g., client name, document title)
     */
    'resourceLabel'?: string | null;
    /**
     * True when the accessor reached for this resource and was **refused** — nothing was disclosed. `action` is then `VIEW`: the entry records the inzage that did not happen, not the capability the caller was exercising.  Only a refusal that names a resource can be recorded. A permission denial identifies none, so it does not appear here at all; see ADR-024 §3e. 
     */
    'denied': boolean;
    /**
     * For mutation actions, contains the changes made. Structure depends on the resource type but typically includes field names with before/after values. 
     */
    'changes'?: { [key: string]: any; } | null;
    /**
     * Client IP address (for access events)
     */
    'ipAddress'?: string | null;
    /**
     * Client User-Agent string (for access events)
     */
    'userAgent'?: string | null;
    /**
     * Admin user ID when the action was performed during an impersonation session. Resolves against the `admin_user` table (admin portal). 
     */
    'impersonatedByUserId'?: string | null;
    /**
     * Display name (or email) of the admin user when the action was performed during an impersonation session. Resolved server-side so the UI can render \"X (geïmpersonifieerd door Y)\" without a second lookup. 
     */
    'impersonatedByName'?: string | null;
    'group'?: AuditLogEntryGroup;
}


/**
 * Consecutive `LIST` rows of one user and one resource type, less than two minutes apart, returned as one entry. 
 */
export interface AuditLogEntryGroup {
    /**
     * Number of audit log rows in the group
     */
    'rowCount': number;
    /**
     * Number of distinct resources the rows name
     */
    'resourceCount': number;
    /**
     * When the group\'s oldest row occurred
     */
    'firstOccurredAt': string;
    /**
     * When the group\'s most recent row occurred
     */
    'lastOccurredAt': string;
}
/**
 * Paginated list of audit log entries
 */
export interface AuditLogPage {
    /**
     * List of audit log entries
     */
    'content': Array<AuditLogEntry>;
    /**
     * Current page number (0-indexed)
     */
    'page': number;
    /**
     * Page size
     */
    'size': number;
    /**
     * Total number of entries matching the filters, counted after grouping — a group is one entry 
     */
    'totalElements': number;
    /**
     * Total number of pages
     */
    'totalPages': number;
}
/**
 * RFC 7807 Problem Details for HTTP APIs
 */
export interface ProblemDetail {
    /**
     * URI reference identifying the problem type
     */
    'type': string;
    /**
     * Short, human-readable summary
     */
    'title': string;
    /**
     * HTTP status code
     */
    'status': number;
    /**
     * Human-readable explanation
     */
    'detail'?: string;
    /**
     * URI reference identifying the specific occurrence
     */
    'instance'?: string;
    /**
     * List of validation errors
     */
    'errors'?: Array<ProblemDetailErrorsInner>;
}
export interface ProblemDetailErrorsInner {
    /**
     * Field name that failed validation
     */
    'field'?: string;
    /**
     * Error message
     */
    'message'?: string;
}

/**
 * AuditLogsApi - axios parameter creator
 */
export const AuditLogsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Every API-koppeling of the organisation, revoked and expired ones included — what they did stays in the log. Feeds the koppeling filter of the audit log, so it is gated on the same permission as the log rather than on managing integrations. 
         * @summary List the API-koppelingen the audit log can be filtered on
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAuditLogApiKoppelingen: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/audit-logs/api-koppelingen`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns a paginated list of audit log entries with optional filtering. Results are sorted by occurrence time (newest first).  **Scoping:** - All entries are scoped to the authenticated user\'s organization - Requires `audit.view` permission  **Filtering:** - Filter by client via `clientId` to see all activity related to a client - Filter by user via `userId` to see all actions performed by a user - Filter by date range via `startDate` and `endDate` - Filter by action type via `action` - Filter by resource type via `resourceType` - Hide reads that disclose nothing beyond the screen via `changesOnly` - Leave out what API-koppelingen did via `includeApiKoppelingen=false`, or narrow to one   koppeling via `apiKoppelingId`  **API-koppelingen:** an integrator that synchronises reads the cliënten and their dossiers on every cycle, which would bury the person-level access this log is reviewed for. So the audit log screen asks for `includeApiKoppelingen=false` until its viewer turns them on. That is presentation only: every row stays recorded (NEN 7513), the parameter defaults to the whole log, and an export or a per-cliënt logging overview (Wabvpz art. 15e) must always include them.  **Grouping (#4803):** a list is logged once per item it shows (ADR-024), so opening one list of 20 cliënten writes 20 `LIST` rows. By default (`groupLists`) consecutive `LIST` rows of one user and one resource type, less than two minutes apart, come back as **one** entry carrying a `group` summary. Grouping is done in the database after every filter has been applied, so `size` and `totalElements` count entries as returned — grouped — and a page is never short because of it. Every other action is one entry per row, `VIEW` included, and so is a `LIST` row with no user behind it (cliënt portal, admin portal). The stored log is not changed: it stays one row per access.  To list the rows behind a group, ask for `groupLists=false` with the group\'s `userId`, `action=LIST`, its `resourceType` and its `firstOccurredAt`/`lastOccurredAt` as `occurredFrom`/`occurredTo`. 
         * @summary List audit log entries
         * @param {string} [clientId] Filter by client ID. When filtering by client, returns audit entries where: - The resource is a Client and matches the ID, OR - The resource belongs to the client (e.g., appointments, documents) 
         * @param {string} [userId] Filter by the user who performed the action
         * @param {string} [startDate] Filter from date (inclusive, ISO 8601 date)
         * @param {string} [endDate] Filter to date (inclusive, ISO 8601 date)
         * @param {AuditAction} [action] Filter by action type
         * @param {string} [resourceType] Filter by resource type (e.g., Client, Appointment, Document)
         * @param {boolean} [changesOnly] Hide &#x60;VIEW&#x60; and &#x60;LIST&#x60; — reads that disclose nothing beyond the screen they were made on. &#x60;DOWNLOAD&#x60; and &#x60;EXPORT&#x60; stay: data leaving the system matters to an auditor even though it changes nothing. 
         * @param {boolean} [groupLists] Return consecutive &#x60;LIST&#x60; rows of one user and one resource type, less than two minutes apart, as one entry with a &#x60;group&#x60; summary. &#x60;false&#x60; returns every row on its own, which is how the rows behind a group are listed. 
         * @param {boolean} [includeApiKoppelingen] Include what API-koppelingen did. &#x60;false&#x60; leaves them out, so the log shows what people did — which is what the audit log screen asks for by default. &#x60;apiKoppelingId&#x60; includes the rows of that one koppeling whatever this says. 
         * @param {string} [apiKoppelingId] Only what this API-koppeling did. A koppeling of another organisation matches nothing. 
         * @param {string} [occurredFrom] Only entries that occurred at or after this instant (inclusive)
         * @param {string} [occurredTo] Only entries that occurred at or before this instant (inclusive)
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAuditLogs: async (clientId?: string, userId?: string, startDate?: string, endDate?: string, action?: AuditAction, resourceType?: string, changesOnly?: boolean, groupLists?: boolean, includeApiKoppelingen?: boolean, apiKoppelingId?: string, occurredFrom?: string, occurredTo?: string, page?: number, size?: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/audit-logs`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (clientId !== undefined) {
                localVarQueryParameter['clientId'] = clientId;
            }

            if (userId !== undefined) {
                localVarQueryParameter['userId'] = userId;
            }

            if (startDate !== undefined) {
                localVarQueryParameter['startDate'] = (startDate as any instanceof Date) ?
                    (startDate as any).toISOString().substring(0,10) :
                    startDate;
            }

            if (endDate !== undefined) {
                localVarQueryParameter['endDate'] = (endDate as any instanceof Date) ?
                    (endDate as any).toISOString().substring(0,10) :
                    endDate;
            }

            if (action !== undefined) {
                localVarQueryParameter['action'] = action;
            }

            if (resourceType !== undefined) {
                localVarQueryParameter['resourceType'] = resourceType;
            }

            if (changesOnly !== undefined) {
                localVarQueryParameter['changesOnly'] = changesOnly;
            }

            if (groupLists !== undefined) {
                localVarQueryParameter['groupLists'] = groupLists;
            }

            if (includeApiKoppelingen !== undefined) {
                localVarQueryParameter['includeApiKoppelingen'] = includeApiKoppelingen;
            }

            if (apiKoppelingId !== undefined) {
                localVarQueryParameter['apiKoppelingId'] = apiKoppelingId;
            }

            if (occurredFrom !== undefined) {
                localVarQueryParameter['occurredFrom'] = (occurredFrom as any instanceof Date) ?
                    (occurredFrom as any).toISOString() :
                    occurredFrom;
            }

            if (occurredTo !== undefined) {
                localVarQueryParameter['occurredTo'] = (occurredTo as any instanceof Date) ?
                    (occurredTo as any).toISOString() :
                    occurredTo;
            }

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AuditLogsApi - functional programming interface
 */
export const AuditLogsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AuditLogsApiAxiosParamCreator(configuration)
    return {
        /**
         * Every API-koppeling of the organisation, revoked and expired ones included — what they did stays in the log. Feeds the koppeling filter of the audit log, so it is gated on the same permission as the log rather than on managing integrations. 
         * @summary List the API-koppelingen the audit log can be filtered on
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listAuditLogApiKoppelingen(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<AuditApiKoppeling>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listAuditLogApiKoppelingen(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AuditLogsApi.listAuditLogApiKoppelingen']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns a paginated list of audit log entries with optional filtering. Results are sorted by occurrence time (newest first).  **Scoping:** - All entries are scoped to the authenticated user\'s organization - Requires `audit.view` permission  **Filtering:** - Filter by client via `clientId` to see all activity related to a client - Filter by user via `userId` to see all actions performed by a user - Filter by date range via `startDate` and `endDate` - Filter by action type via `action` - Filter by resource type via `resourceType` - Hide reads that disclose nothing beyond the screen via `changesOnly` - Leave out what API-koppelingen did via `includeApiKoppelingen=false`, or narrow to one   koppeling via `apiKoppelingId`  **API-koppelingen:** an integrator that synchronises reads the cliënten and their dossiers on every cycle, which would bury the person-level access this log is reviewed for. So the audit log screen asks for `includeApiKoppelingen=false` until its viewer turns them on. That is presentation only: every row stays recorded (NEN 7513), the parameter defaults to the whole log, and an export or a per-cliënt logging overview (Wabvpz art. 15e) must always include them.  **Grouping (#4803):** a list is logged once per item it shows (ADR-024), so opening one list of 20 cliënten writes 20 `LIST` rows. By default (`groupLists`) consecutive `LIST` rows of one user and one resource type, less than two minutes apart, come back as **one** entry carrying a `group` summary. Grouping is done in the database after every filter has been applied, so `size` and `totalElements` count entries as returned — grouped — and a page is never short because of it. Every other action is one entry per row, `VIEW` included, and so is a `LIST` row with no user behind it (cliënt portal, admin portal). The stored log is not changed: it stays one row per access.  To list the rows behind a group, ask for `groupLists=false` with the group\'s `userId`, `action=LIST`, its `resourceType` and its `firstOccurredAt`/`lastOccurredAt` as `occurredFrom`/`occurredTo`. 
         * @summary List audit log entries
         * @param {string} [clientId] Filter by client ID. When filtering by client, returns audit entries where: - The resource is a Client and matches the ID, OR - The resource belongs to the client (e.g., appointments, documents) 
         * @param {string} [userId] Filter by the user who performed the action
         * @param {string} [startDate] Filter from date (inclusive, ISO 8601 date)
         * @param {string} [endDate] Filter to date (inclusive, ISO 8601 date)
         * @param {AuditAction} [action] Filter by action type
         * @param {string} [resourceType] Filter by resource type (e.g., Client, Appointment, Document)
         * @param {boolean} [changesOnly] Hide &#x60;VIEW&#x60; and &#x60;LIST&#x60; — reads that disclose nothing beyond the screen they were made on. &#x60;DOWNLOAD&#x60; and &#x60;EXPORT&#x60; stay: data leaving the system matters to an auditor even though it changes nothing. 
         * @param {boolean} [groupLists] Return consecutive &#x60;LIST&#x60; rows of one user and one resource type, less than two minutes apart, as one entry with a &#x60;group&#x60; summary. &#x60;false&#x60; returns every row on its own, which is how the rows behind a group are listed. 
         * @param {boolean} [includeApiKoppelingen] Include what API-koppelingen did. &#x60;false&#x60; leaves them out, so the log shows what people did — which is what the audit log screen asks for by default. &#x60;apiKoppelingId&#x60; includes the rows of that one koppeling whatever this says. 
         * @param {string} [apiKoppelingId] Only what this API-koppeling did. A koppeling of another organisation matches nothing. 
         * @param {string} [occurredFrom] Only entries that occurred at or after this instant (inclusive)
         * @param {string} [occurredTo] Only entries that occurred at or before this instant (inclusive)
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listAuditLogs(clientId?: string, userId?: string, startDate?: string, endDate?: string, action?: AuditAction, resourceType?: string, changesOnly?: boolean, groupLists?: boolean, includeApiKoppelingen?: boolean, apiKoppelingId?: string, occurredFrom?: string, occurredTo?: string, page?: number, size?: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AuditLogPage>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listAuditLogs(clientId, userId, startDate, endDate, action, resourceType, changesOnly, groupLists, includeApiKoppelingen, apiKoppelingId, occurredFrom, occurredTo, page, size, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AuditLogsApi.listAuditLogs']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * AuditLogsApi - factory interface
 */
export const AuditLogsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AuditLogsApiFp(configuration)
    return {
        /**
         * Every API-koppeling of the organisation, revoked and expired ones included — what they did stays in the log. Feeds the koppeling filter of the audit log, so it is gated on the same permission as the log rather than on managing integrations. 
         * @summary List the API-koppelingen the audit log can be filtered on
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAuditLogApiKoppelingen(options?: RawAxiosRequestConfig): AxiosPromise<Array<AuditApiKoppeling>> {
            return localVarFp.listAuditLogApiKoppelingen(options).then((request) => request(axios, basePath));
        },
        /**
         * Returns a paginated list of audit log entries with optional filtering. Results are sorted by occurrence time (newest first).  **Scoping:** - All entries are scoped to the authenticated user\'s organization - Requires `audit.view` permission  **Filtering:** - Filter by client via `clientId` to see all activity related to a client - Filter by user via `userId` to see all actions performed by a user - Filter by date range via `startDate` and `endDate` - Filter by action type via `action` - Filter by resource type via `resourceType` - Hide reads that disclose nothing beyond the screen via `changesOnly` - Leave out what API-koppelingen did via `includeApiKoppelingen=false`, or narrow to one   koppeling via `apiKoppelingId`  **API-koppelingen:** an integrator that synchronises reads the cliënten and their dossiers on every cycle, which would bury the person-level access this log is reviewed for. So the audit log screen asks for `includeApiKoppelingen=false` until its viewer turns them on. That is presentation only: every row stays recorded (NEN 7513), the parameter defaults to the whole log, and an export or a per-cliënt logging overview (Wabvpz art. 15e) must always include them.  **Grouping (#4803):** a list is logged once per item it shows (ADR-024), so opening one list of 20 cliënten writes 20 `LIST` rows. By default (`groupLists`) consecutive `LIST` rows of one user and one resource type, less than two minutes apart, come back as **one** entry carrying a `group` summary. Grouping is done in the database after every filter has been applied, so `size` and `totalElements` count entries as returned — grouped — and a page is never short because of it. Every other action is one entry per row, `VIEW` included, and so is a `LIST` row with no user behind it (cliënt portal, admin portal). The stored log is not changed: it stays one row per access.  To list the rows behind a group, ask for `groupLists=false` with the group\'s `userId`, `action=LIST`, its `resourceType` and its `firstOccurredAt`/`lastOccurredAt` as `occurredFrom`/`occurredTo`. 
         * @summary List audit log entries
         * @param {string} [clientId] Filter by client ID. When filtering by client, returns audit entries where: - The resource is a Client and matches the ID, OR - The resource belongs to the client (e.g., appointments, documents) 
         * @param {string} [userId] Filter by the user who performed the action
         * @param {string} [startDate] Filter from date (inclusive, ISO 8601 date)
         * @param {string} [endDate] Filter to date (inclusive, ISO 8601 date)
         * @param {AuditAction} [action] Filter by action type
         * @param {string} [resourceType] Filter by resource type (e.g., Client, Appointment, Document)
         * @param {boolean} [changesOnly] Hide &#x60;VIEW&#x60; and &#x60;LIST&#x60; — reads that disclose nothing beyond the screen they were made on. &#x60;DOWNLOAD&#x60; and &#x60;EXPORT&#x60; stay: data leaving the system matters to an auditor even though it changes nothing. 
         * @param {boolean} [groupLists] Return consecutive &#x60;LIST&#x60; rows of one user and one resource type, less than two minutes apart, as one entry with a &#x60;group&#x60; summary. &#x60;false&#x60; returns every row on its own, which is how the rows behind a group are listed. 
         * @param {boolean} [includeApiKoppelingen] Include what API-koppelingen did. &#x60;false&#x60; leaves them out, so the log shows what people did — which is what the audit log screen asks for by default. &#x60;apiKoppelingId&#x60; includes the rows of that one koppeling whatever this says. 
         * @param {string} [apiKoppelingId] Only what this API-koppeling did. A koppeling of another organisation matches nothing. 
         * @param {string} [occurredFrom] Only entries that occurred at or after this instant (inclusive)
         * @param {string} [occurredTo] Only entries that occurred at or before this instant (inclusive)
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAuditLogs(clientId?: string, userId?: string, startDate?: string, endDate?: string, action?: AuditAction, resourceType?: string, changesOnly?: boolean, groupLists?: boolean, includeApiKoppelingen?: boolean, apiKoppelingId?: string, occurredFrom?: string, occurredTo?: string, page?: number, size?: number, options?: RawAxiosRequestConfig): AxiosPromise<AuditLogPage> {
            return localVarFp.listAuditLogs(clientId, userId, startDate, endDate, action, resourceType, changesOnly, groupLists, includeApiKoppelingen, apiKoppelingId, occurredFrom, occurredTo, page, size, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * AuditLogsApi - object-oriented interface
 */
export class AuditLogsApi extends BaseAPI {
    /**
     * Every API-koppeling of the organisation, revoked and expired ones included — what they did stays in the log. Feeds the koppeling filter of the audit log, so it is gated on the same permission as the log rather than on managing integrations. 
     * @summary List the API-koppelingen the audit log can be filtered on
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listAuditLogApiKoppelingen(options?: RawAxiosRequestConfig) {
        return AuditLogsApiFp(this.configuration).listAuditLogApiKoppelingen(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns a paginated list of audit log entries with optional filtering. Results are sorted by occurrence time (newest first).  **Scoping:** - All entries are scoped to the authenticated user\'s organization - Requires `audit.view` permission  **Filtering:** - Filter by client via `clientId` to see all activity related to a client - Filter by user via `userId` to see all actions performed by a user - Filter by date range via `startDate` and `endDate` - Filter by action type via `action` - Filter by resource type via `resourceType` - Hide reads that disclose nothing beyond the screen via `changesOnly` - Leave out what API-koppelingen did via `includeApiKoppelingen=false`, or narrow to one   koppeling via `apiKoppelingId`  **API-koppelingen:** an integrator that synchronises reads the cliënten and their dossiers on every cycle, which would bury the person-level access this log is reviewed for. So the audit log screen asks for `includeApiKoppelingen=false` until its viewer turns them on. That is presentation only: every row stays recorded (NEN 7513), the parameter defaults to the whole log, and an export or a per-cliënt logging overview (Wabvpz art. 15e) must always include them.  **Grouping (#4803):** a list is logged once per item it shows (ADR-024), so opening one list of 20 cliënten writes 20 `LIST` rows. By default (`groupLists`) consecutive `LIST` rows of one user and one resource type, less than two minutes apart, come back as **one** entry carrying a `group` summary. Grouping is done in the database after every filter has been applied, so `size` and `totalElements` count entries as returned — grouped — and a page is never short because of it. Every other action is one entry per row, `VIEW` included, and so is a `LIST` row with no user behind it (cliënt portal, admin portal). The stored log is not changed: it stays one row per access.  To list the rows behind a group, ask for `groupLists=false` with the group\'s `userId`, `action=LIST`, its `resourceType` and its `firstOccurredAt`/`lastOccurredAt` as `occurredFrom`/`occurredTo`. 
     * @summary List audit log entries
     * @param {string} [clientId] Filter by client ID. When filtering by client, returns audit entries where: - The resource is a Client and matches the ID, OR - The resource belongs to the client (e.g., appointments, documents) 
     * @param {string} [userId] Filter by the user who performed the action
     * @param {string} [startDate] Filter from date (inclusive, ISO 8601 date)
     * @param {string} [endDate] Filter to date (inclusive, ISO 8601 date)
     * @param {AuditAction} [action] Filter by action type
     * @param {string} [resourceType] Filter by resource type (e.g., Client, Appointment, Document)
     * @param {boolean} [changesOnly] Hide &#x60;VIEW&#x60; and &#x60;LIST&#x60; — reads that disclose nothing beyond the screen they were made on. &#x60;DOWNLOAD&#x60; and &#x60;EXPORT&#x60; stay: data leaving the system matters to an auditor even though it changes nothing. 
     * @param {boolean} [groupLists] Return consecutive &#x60;LIST&#x60; rows of one user and one resource type, less than two minutes apart, as one entry with a &#x60;group&#x60; summary. &#x60;false&#x60; returns every row on its own, which is how the rows behind a group are listed. 
     * @param {boolean} [includeApiKoppelingen] Include what API-koppelingen did. &#x60;false&#x60; leaves them out, so the log shows what people did — which is what the audit log screen asks for by default. &#x60;apiKoppelingId&#x60; includes the rows of that one koppeling whatever this says. 
     * @param {string} [apiKoppelingId] Only what this API-koppeling did. A koppeling of another organisation matches nothing. 
     * @param {string} [occurredFrom] Only entries that occurred at or after this instant (inclusive)
     * @param {string} [occurredTo] Only entries that occurred at or before this instant (inclusive)
     * @param {number} [page] Page number (0-indexed)
     * @param {number} [size] Page size
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listAuditLogs(clientId?: string, userId?: string, startDate?: string, endDate?: string, action?: AuditAction, resourceType?: string, changesOnly?: boolean, groupLists?: boolean, includeApiKoppelingen?: boolean, apiKoppelingId?: string, occurredFrom?: string, occurredTo?: string, page?: number, size?: number, options?: RawAxiosRequestConfig) {
        return AuditLogsApiFp(this.configuration).listAuditLogs(clientId, userId, startDate, endDate, action, resourceType, changesOnly, groupLists, includeApiKoppelingen, apiKoppelingId, occurredFrom, occurredTo, page, size, options).then((request) => request(this.axios, this.basePath));
    }
}



